import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import GlobalFooter from '../components/GlobalFooter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Camera, ArrowLeft, X, Search, AlertTriangle, 
  Loader2, Pill, Shield, Clock, FlaskConical, 
  History, Trash2, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Result card component
const SupplementResult = ({ supplement, onClose, onCheckInteractions }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-2xl p-6 border border-border"
    data-testid="supplement-result"
  >
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Pill className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-[#1A1A1A]">{supplement.name}</h3>
          <div className="flex items-center gap-2">
            <p className="text-sm text-muted-foreground">{supplement.category}</p>
            {supplement.source && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                supplement.source === 'ai_vision' ? 'bg-violet-100 text-violet-700' :
                supplement.source === 'ai' ? 'bg-violet-100 text-violet-700' :
                'bg-green-100 text-green-700'
              }`}>
                {supplement.source === 'ai_vision' ? 'AI Vision' : supplement.source === 'ai' ? 'AI Analysis' : 'Database'}
              </span>
            )}
          </div>
        </div>
      </div>
      <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" data-testid="close-result-btn">
        <X className="w-5 h-5 text-muted-foreground" />
      </button>
    </div>
    
    <div className="space-y-4">
      {supplement.benefits && (
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase mb-1">Benefits</p>
          <p className="text-sm text-[#1A1A1A]">{supplement.benefits}</p>
        </div>
      )}

      {supplement.ingredients && (
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase mb-1">Active Ingredients</p>
          <p className="text-sm text-[#1A1A1A]">{supplement.ingredients}</p>
        </div>
      )}
      
      {supplement.dosage && (
        <div className="flex items-start gap-3">
          <FlaskConical className="w-4 h-4 text-muted-foreground mt-0.5" />
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase mb-1">Typical Dosage</p>
            <p className="text-sm text-[#1A1A1A]">{supplement.dosage}</p>
          </div>
        </div>
      )}
      
      {supplement.timing && (
        <div className="flex items-start gap-3">
          <Clock className="w-4 h-4 text-muted-foreground mt-0.5" />
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase mb-1">Best Time to Take</p>
            <p className="text-sm text-[#1A1A1A]">{supplement.timing}</p>
          </div>
        </div>
      )}

      {supplement.evidence_grade && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground uppercase">Evidence</span>
          <span className={`text-xs font-bold px-2 py-0.5 rounded ${
            supplement.evidence_grade === 'A' ? 'bg-green-100 text-green-700' :
            supplement.evidence_grade === 'B' ? 'bg-yellow-100 text-yellow-700' :
            'bg-orange-100 text-orange-700'
          }`}>Grade {supplement.evidence_grade}</span>
        </div>
      )}
      
      {supplement.warnings && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <p className="text-sm font-medium text-amber-900">Warnings</p>
          </div>
          <p className="text-xs text-amber-800">
            {typeof supplement.warnings === 'string' ? supplement.warnings : supplement.warnings.map((w, i) => <span key={i}>- {w}<br/></span>)}
          </p>
        </div>
      )}
    </div>
    
    <div className="mt-6 space-y-2">
      <Button 
        variant="outline" 
        className="w-full"
        onClick={onCheckInteractions}
        data-testid="check-interactions-btn"
      >
        <Shield className="w-4 h-4 mr-2" />
        Check Interactions
      </Button>
      <Button 
        className="w-full btn-primary"
        onClick={() => window.location.href = `/supplements`}
      >
        View All Supplements
      </Button>
    </div>
  </motion.div>
);


export default function ScannerPage() {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  const [cameraActive, setCameraActive] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [scannedResult, setScannedResult] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [manualSearch, setManualSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [scanHistory, setScanHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [aiSearchName, setAiSearchName] = useState('');
  const [aiAnalyzing, setAiAnalyzing] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch scan history
  const fetchHistory = useCallback(async () => {
    if (!user) return;
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('vitala_token');
      const res = await axios.get(`${API}/scanner/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScanHistory(res.data || []);
    } catch (err) {
      console.error('Failed to fetch scan history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchHistory();
    return () => { stopCamera(); };
  }, [fetchHistory]);

  // Save to history
  const saveToHistory = useCallback(async (supplement, barcode) => {
    if (!user) return;
    try {
      const token = localStorage.getItem('vitala_token');
      await axios.post(`${API}/scanner/history`, {
        name: supplement.name,
        category: supplement.category || 'Supplement',
        benefits: supplement.benefits || '',
        dosage: supplement.dosage || '',
        timing: supplement.timing || '',
        barcode: barcode || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchHistory();
    } catch (err) {
      console.error('Failed to save to history:', err);
    }
  }, [user, fetchHistory]);

  // Start camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      setCameraActive(true);
      // Wait for next render so video element is in DOM, then attach stream
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError') {
        toast.error('Camera access denied. Please allow camera in browser settings.');
      } else if (err.name === 'NotFoundError') {
        toast.error('No camera found on this device.');
      } else {
        toast.error('Could not access camera. Try using manual search.');
      }
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  // Capture photo and analyze
  const captureAndAnalyze = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    const base64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
    setCapturedImage(canvas.toDataURL('image/jpeg', 0.8));
    stopCamera();
    setAnalyzing(true);

    try {
      const token = localStorage.getItem('vitala_token');
      const response = await axios.post(`${API}/scanner/photo-analyze`,
        { image_base64: base64 },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 25000 }
      );

      if (response.data?.found) {
        setScannedResult(response.data);
        saveToHistory(response.data, null);
        toast.success('Supplement identified!');
      } else {
        toast.error(response.data?.error || 'Could not identify supplement. Try a clearer photo or manual search.');
      }
    } catch (err) {
      console.error('Photo analysis failed:', err);
      toast.error('Analysis failed. Please try again or use manual search.');
    } finally {
      setAnalyzing(false);
    }
  };

  // Manual text search against local database
  const handleManualSearch = async () => {
    if (!manualSearch.trim()) return;
    setSearching(true);
    try {
      const response = await axios.get(`${API}/supplements/search?q=${encodeURIComponent(manualSearch)}`);
      setSearchResults(response.data || []);
      if (response.data?.length === 0) {
        toast.info('No matches in database. Try AI analysis below.');
      }
    } catch (err) {
      toast.error('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  // AI analysis by name (typed)
  const analyzeByName = async () => {
    if (!aiSearchName.trim()) return;
    setAiAnalyzing(true);
    try {
      const token = localStorage.getItem('vitala_token');
      const response = await axios.post(`${API}/scanner/analyze`,
        { name: aiSearchName.trim() },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 }
      );
      if (response.data?.found) {
        setScannedResult(response.data);
        saveToHistory(response.data, null);
        toast.success('AI analysis complete!');
      } else {
        toast.error('Could not analyze. Try a different name.');
      }
    } catch (err) {
      toast.error('AI analysis failed. Please try again.');
    } finally {
      setAiAnalyzing(false);
    }
  };

  // Delete scan history item
  const deleteHistoryItem = async (id) => {
    try {
      const token = localStorage.getItem('vitala_token');
      await axios.delete(`${API}/scanner/history/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScanHistory(prev => prev.filter(item => item.id !== id));
      toast.success('Removed from history');
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const resetScanner = () => {
    setScannedResult(null);
    setCapturedImage(null);
    setSearchResults([]);
    setAiSearchName('');
  };

  const isCameraSupported = typeof navigator !== 'undefined' && 'mediaDevices' in navigator;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <canvas ref={canvasRef} className="hidden" />

      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="small" linkTo="/" />
          <div className="w-16" />
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full">
        {/* Hero — hidden when camera is active */}
        {!cameraActive && !analyzing && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-2">Supplement Lookup</h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              Snap a photo of any supplement label and our AI will instantly identify it.
            </p>
          </motion.div>
        )}

        {/* Main Scanner / Result Area */}
        <AnimatePresence mode="wait">
          {scannedResult ? (
            <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mb-8">
              <SupplementResult
                supplement={scannedResult}
                onClose={resetScanner}
                onCheckInteractions={() => navigate('/quiz')}
              />
              <div className="mt-4 text-center">
                <Button variant="outline" onClick={() => { resetScanner(); startCamera(); }} data-testid="scan-another-btn">
                  <Camera className="w-4 h-4 mr-2" />
                  Lookup Another
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div key="scanner" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-8">
              {cameraActive ? (
                <div className="relative rounded-2xl overflow-hidden bg-black" data-testid="camera-active" style={{ height: 'calc(100vh - 180px)', maxHeight: '500px' }}>
                  <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                  
                  {/* Instruction overlay at top */}
                  <div className="absolute top-3 left-3 right-3 z-10">
                    <div className="bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2 text-center">
                      <p className="text-sm font-medium text-white">Point at the supplement label & tap capture</p>
                    </div>
                  </div>

                  {/* Capture controls at bottom — always visible */}
                  <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/70 to-transparent pt-8 pb-4 flex justify-center gap-6 items-center">
                    <button
                      onClick={captureAndAnalyze}
                      className="w-18 h-18 rounded-full bg-white border-4 border-primary flex items-center justify-center hover:scale-105 active:scale-95 transition-transform shadow-lg"
                      style={{ width: '72px', height: '72px' }}
                      data-testid="capture-photo-btn"
                    >
                      <Camera className="w-8 h-8 text-primary" />
                    </button>
                    <button
                      onClick={stopCamera}
                      className="absolute right-4 bottom-4 px-4 py-2 rounded-full bg-white/20 text-white text-sm font-medium hover:bg-white/30 transition-colors"
                      data-testid="stop-camera-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : analyzing ? (
                <div className="bg-white rounded-2xl border border-border p-8 text-center" data-testid="analyzing-state">
                  {capturedImage && (
                    <img src={capturedImage} alt="Captured supplement" className="w-48 h-36 object-cover rounded-xl mx-auto mb-4 border border-border" />
                  )}
                  <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
                  <p className="font-medium text-[#1A1A1A]">Analyzing supplement...</p>
                  <p className="text-sm text-muted-foreground mt-1">AI is reading the label</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-border p-8 text-center">
                  {isCameraSupported ? (
                    <>
                      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <Camera className="w-10 h-10 text-primary" />
                      </div>
                      <p className="font-medium text-[#1A1A1A] mb-1">Photo Recognition</p>
                      <p className="text-sm text-muted-foreground mb-6">
                        Point at a supplement bottle and tap to capture. AI reads the label instantly.
                      </p>
                      <Button onClick={startCamera} className="btn-primary" data-testid="open-camera-btn">
                        <Camera className="w-4 h-4 mr-2" />
                        Open Camera
                      </Button>
                    </>
                  ) : (
                    <div>
                      <p className="text-muted-foreground mb-2">Camera not available on this device</p>
                      <p className="text-sm text-muted-foreground">Use AI search below to look up supplements</p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Unified Search — always visible when no result */}
        {!scannedResult && !cameraActive && !analyzing && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-8">
            <div className="bg-white border border-border rounded-2xl p-6" data-testid="ai-search-section">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold text-[#1A1A1A]">Search by Name</p>
                  <p className="text-xs text-muted-foreground">AI-powered analysis for any supplement</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Input
                  value={aiSearchName}
                  onChange={(e) => setAiSearchName(e.target.value)}
                  placeholder="e.g., Vitamin D3, Ashwagandha, Fish Oil..."
                  className="flex-1"
                  data-testid="ai-supplement-name-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') analyzeByName(); }}
                  disabled={aiAnalyzing}
                />
                <Button
                  onClick={analyzeByName}
                  disabled={!aiSearchName.trim() || aiAnalyzing}
                  className="btn-primary"
                  data-testid="ai-analyze-btn"
                >
                  {aiAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Scan History */}
        {scanHistory.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mb-8">
            <div className="bg-white rounded-2xl border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="font-medium text-[#1A1A1A] flex items-center gap-2">
                  <History className="w-4 h-4" /> Recent Lookups
                </p>
                <span className="text-xs text-muted-foreground">{scanHistory.length} items</span>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {scanHistory.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 rounded-xl border border-border hover:bg-gray-50 transition-colors" data-testid={`history-item-${item.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.category} - {new Date(item.scanned_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteHistoryItem(item.id)}
                      className="ml-2 p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid={`delete-history-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* How it works */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-6 border border-primary/10">
            <h3 className="font-semibold text-[#1A1A1A] mb-4">How It Works</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: Camera, title: 'Snap a Photo', desc: 'Point your camera at any supplement bottle or label' },
                { icon: Sparkles, title: 'AI Analyzes', desc: 'Our AI reads the label and identifies the product' },
                { icon: Shield, title: 'Get Details', desc: 'View benefits, dosage, timing, and safety warnings' },
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <step.icon className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A]">{step.title}</p>
                    <p className="text-xs text-muted-foreground">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </main>

      <GlobalFooter />
    </div>
  );
}
